import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import interiorLibraryAdminApi from "../services/interiorLibraryAdminApi";

import { createInstanceNode } from "../schema/instanceNode.schema";
import { canDefinitionContain } from "../utils/definitionRules";
import HierarchySceneCanvas from "../components/definition-editor/HierarchySceneCanvas";
import EditorRightPanel from "../components/editor-right/EditorRightPanel";
import { resolveNodeTree } from "../engine/resolveNodeTree";
import ParameterManagerModal from "../components/definition-editor/ParameterManagerModal";
import { buildQuickAddCreationOptions } from "../utils/editorCabinetUtils";
import { createRootInstanceFromDefinition } from "../utils/createRootInstanceFromDefinition";
import { reIdNodeTree, findNodeInTree, collectNodesByIds, removeNodesByIds,} from "../utils/editorTreeUtils";
import useEditorHistory from "../hooks/useEditorHistory";
import useEditorKeyboardShortcuts from "../hooks/useEditorKeyboardShortcuts";
import useInteriorProjectData, { DEFAULT_PROJECT_PARAMETERS,} from "../hooks/useInteriorProjectData";
import EditorTopBar from "../components/editor-shell/EditorTopBar";
import EditorRibbon from "../components/editor-shell/EditorRibbon";
import EditorLeftPanel from "../components/editor-left/EditorLeftPanel";
import SelectionActionBar from "../components/editor-shell/SelectionActionBar";
import { executeSceneEditActions } from "../ai/executeSceneEditActions";
import { createRoomDefaults, createLRoomPreset } from "../room/roomSchema";
import { resolveWallPlacement } from "../room/resolveWallPlacement";
import DrawingSheetPage from "../documentation/components/sheet/DrawingSheetPage";



function isBoardDefinition(def) {
  return (
    def &&
    def.nodeType === "element" &&
    String(def.category || "").startsWith("board.")
  );
}

function findResolvedNodeById(tree = [], id) {
  for (const node of tree || []) {
    if (String(node.id) === String(id)) return node;

    const found = findResolvedNodeById(node.children || [], id);
    if (found) return found;
  }

  return null;
}

export default function InteriorEditorPage() {
  const navigate = useNavigate();
  const { projectId } = useParams();

  const [selectedIds, setSelectedIds] = useState([]);
  const [clipboard, setClipboard] = useState([]);

  const [project, setProject] = useState(null);
  const [definitions, setDefinitions] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [resolvedNode, setResolvedNode] = useState(null);
  const [selectedPartition, setSelectedPartition] = useState(null);

  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [projectParameters, setProjectParameters] = useState(DEFAULT_PROJECT_PARAMETERS);
  const [room, setRoom] = useState(createRoomDefaults());
  const [selectedRoomObjectId, setSelectedRoomObjectId] = useState(null);

  const [leftTab, setLeftTab] = useState("browser");

  const [toolMode, setToolMode] = useState("select");
const [viewMode, setViewMode] = useState("realistic");
const [isolateMode, setIsolateMode] = useState(false);
const [workspaceMode, setWorkspaceMode] = useState("design");

const [showParameterManager, setShowParameterManager] = useState(false);
const [showMountingDrillings, setShowMountingDrillings] = useState(false);
const [templates, setTemplates] = useState([]);
const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
const [templateForm, setTemplateForm] = useState({
  name: "",
  code: "",
  category: "base.cabinet",
  description: "",
});
const [categories, setCategories] = useState([]);
const [materials, setMaterials] = useState([]);

const [documentation, setDocumentation] = useState(null);

const [sceneViewSettings, setSceneViewSettings] = useState({
  cameraType: "perspective",
  showGrid: true,
  showGizmo: true,
  showCoordinateSystems: false,
  ambientIntensity: 0.9,
  mainLightIntensity: 1,
  requestedView: null,
  requestedAction: null,
  disableAutoFocusSelected: true,
orbitAroundSelection: true,

disableAutoFocusSelected: true,
orbitAroundSelectionWithoutCenter: true,
});

const {
  history,
  future,
  applyProjectChange,
  undo,
  redo,
  clearHistory,
} = useEditorHistory({
  nodes,
  setNodes,
  projectParameters,
  setProjectParameters,
  onAfterRestore: () => {
    setResolvedNode(null);
  },
});

const {
  loading,
  saving,
  loadProject,
  saveProject,
  deleteProject,
} = useInteriorProjectData({
  projectId,
  onLoaded: ({
    project,
    definitions,
    templates,
    categories,
    nodes,
    projectParameters,
    projectName,
    projectDescription,
    materials,
  }) => {
    setProject(project);
    setDefinitions(definitions);
    setTemplates(templates);
    setCategories(categories);
    setNodes(nodes);
    setProjectParameters(projectParameters);
    setProjectName(projectName);
    setProjectDescription(projectDescription);
    setMaterials(materials);
    setRoom(project?.scene?.room || createRoomDefaults());
    setDocumentation(project?.scene?.documentation || null);
  },
});

function applyWorkspaceMode(mode) {
  setWorkspaceMode(mode);

  if (mode === "design") {
    setToolMode("select");
    setViewMode("full");
    setIsolateMode(false);
    return;
  }

  if (mode === "inspect") {
    setToolMode("select");
    setViewMode("xray");
    setIsolateMode(true);
    return;
  }

  if (mode === "grid") {
    setToolMode("partition");
    setViewMode("box");
    setIsolateMode(true);
    return;
  }

  if (mode === "present") {
    setToolMode("select");
    setViewMode("realistic");
    setIsolateMode(false);
    return;
  }

  if (mode === "documentation") {
    setToolMode("select");
    setViewMode("hiddenLine");
    setIsolateMode(false);
    return;
  }

  setWorkspaceMode("custom");
}

function handleApplySceneEditActions(editResult) {
  executeSceneEditActions({
    editResult,
    selectedNode,
    resolvedNode,
    simpleSelectedNode,
    simpleResolvedNode,
    resolvedNodes,
    applyNodePatch,
    applyProjectParametersChange,
    handleQuickAdd,
    removeSelectedNode,
  });
}

function getSelectedNodes() {
  const ids = selectedIds.length ? selectedIds : selectedId ? [selectedId] : [];
  return collectNodesByIds(nodes, ids);
}

function addTemplateInstance(template) {
  const rootNode = template.template_json?.rootNode;
  if (!rootNode) {
    alert("Template has no root node.");
    return;
  }

  const node = reIdNodeTree(rootNode);

  applyProjectChange(() => {
    setNodes((prev) => [...prev, node]);
  });

  setSelectedId(node.id);
}

  

  async function saveSelectedAsTemplate() {
  if (!resolvedNode) {
    alert("Select element first.");
    return;
  }

  const rootNode = selectedNode || resolvedNode;

  if (!rootNode?.definitionId) {
    alert("Selected element cannot be saved as template.");
    return;
  }

  try {
    const payload = {
      code: templateForm.code,
      name: templateForm.name,
      category: templateForm.category,
      description: templateForm.description,
      base_definition_id: rootNode.definitionId,
      template_json: {
        rootNode: structuredClone(rootNode),
      },
      status: "active",
    };

    await interiorLibraryAdminApi.createTemplate(payload);

    const res = await interiorLibraryAdminApi.listTemplates();
    setTemplates(res.items || []);

    setShowSaveTemplateModal(false);
  } catch (err) {
    console.error("save template error:", err);
    alert(err?.response?.data?.message || "Failed to save template.");
  }
}

useEffect(() => {
  loadProject();
}, [loadProject]);

  useEffect(() => {
    setNodes((prev) => [...prev]);
  }, [projectParameters]);

  function openDefinitionEditorById(defId) {
    const def = definitions.find((d) => String(d.id) === String(defId));
    if (!def) return;

    if (isBoardDefinition(def)) {
      navigate(`/interior-editor/definitions/${def.id}/board`);
    } else {
      navigate(`/interior-editor/definitions/${def.id}/hierarchy`);
    }
  }

  

  function openResolvedNodeEditor(node, preferred = "auto") {
  if (!node) return;

  const nodeType = String(node.nodeType || "").toLowerCase();

  // 1. Regular definition node
  if (preferred === "definition" || (preferred === "auto" && node.definitionId)) {
    if (node.definitionId) {
      openDefinitionEditorById(node.definitionId);
      return;
    }
  }

  // 2. Item assembly frame: source item mounting / assembly
  if (nodeType === "item_assembly_frame") {
    const itemId = node.source?.itemId || node.item_mounting?.item_id;

    if (!itemId) {
      alert("This item assembly frame has no linked source item.");
      return;
    }

    if (preferred === "assembly") {
      navigate(`/interior-editor/catalog/items/${itemId}/assembly`);
      return;
    }

    navigate(`/interior-editor/catalog/items/${itemId}/mounting`);
    return;
  }

  // 3. Asset component: can open several source editors
  if (nodeType === "asset_component") {
    const sourceItemId = node.source?.itemId;
    const componentItemId = node.component_item?.id;
    const assetId = node.asset?.id;

    if (preferred === "component-item-mounting" && componentItemId) {
      navigate(`/interior-editor/catalog/items/${componentItemId}/mounting`);
      return;
    }

    if (preferred === "asset-mounting" && assetId) {
      navigate(`/interior-editor/assets/${assetId}/mounting`);
      return;
    }

    if (preferred === "assembly" && sourceItemId) {
      navigate(`/interior-editor/catalog/items/${sourceItemId}/assembly`);
      return;
    }

    // default za asset_component: otvori item assembly gde se podešava position/rotation
    if (sourceItemId) {
      navigate(`/interior-editor/catalog/items/${sourceItemId}/assembly`);
      return;
    }

    alert("This generated component has no source item link.");
    return;
  }

  // 4. Mounting item / screw
  if (nodeType === "mounting_item") {
    const scope = node.source?.mountingScope;
    const itemId = node.source?.itemId;
    const assetId = node.source?.assetId;

    // Ako je šraf iz asset_mounting-a, obično želiš asset mounting editor parent asseta.
    // Ako nemamo parent asset u source-u, otvorimo sam item kao fallback.
    if (scope === "asset_mounting" && node.asset?.id) {
      navigate(`/interior-editor/assets/${node.asset.id}/mounting`);
      return;
    }

    if (itemId) {
      navigate(`/interior-editor/catalog/items/${itemId}/mounting`);
      return;
    }

    if (assetId) {
      navigate(`/interior-editor/assets/${assetId}/mounting`);
      return;
    }

    alert("This mounting item has no editable source link.");
    return;
  }

  // fallback
  if (node.definitionId) {
    openDefinitionEditorById(node.definitionId);
  }
}

  function updateNodeInTree(tree, nodeId, updater) {
    return (tree || []).map((node) => {
      if (String(node.id) === String(nodeId)) return updater(node);

      return {
        ...node,
        children: updateNodeInTree(node.children || [], nodeId, updater),
      };
    });
  }

  function applyMaterialToTree(node, materialAssignments) {
  const category = String(node.category || node.definition?.category || "").toLowerCase();
  const name = String(node.name || "").toLowerCase();

  const isBoard =
    category.startsWith("board.") ||
    name.includes("board") ||
    name.includes("panel");

    

  return {
    ...node,
    materialAssignments: isBoard
      ? materialAssignments
      : node.materialAssignments,
    children: (node.children || []).map((child) =>
      applyMaterialToTree(child, materialAssignments)
    ),
  };
}

function handleQuickAdd(direction, options = {}) {
  const sourceNode = simpleSelectedNode || selectedNode;
  const sourceResolvedNode = simpleResolvedNode || resolvedNode;

  if (!sourceNode || !sourceResolvedNode) return;

  const selectedDefinition = definitions.find(
    (d) => String(d.id) === String(sourceNode.definitionId)
  );

  if (!selectedDefinition) return;

  const creationOptions = buildQuickAddCreationOptions({
    direction,
    options,
    selectedNode: sourceNode,
    resolvedNode: sourceResolvedNode,
  });

  if (!creationOptions) return;

  addRootInstance(selectedDefinition, creationOptions);
}

function applyNodePatch(patch) {
  if (!patch?.id) return;

  applyProjectChange(() => {
    setNodes((prev) =>
      updateNodeInTree(prev, patch.id, (node) => {
        if (patch.applyMaterialToChildren) {
          return {
            ...node,
            materialAssignments: patch.materialAssignments,
            children: (node.children || []).map((child) =>
              applyMaterialToTree(child, patch.materialAssignments)
            ),
          };
        }

        return {
          ...node,
          ...patch,
        };
      })
    );
  });

  setResolvedNode((prev) => {
    if (!prev) return prev;

    if (String(prev.id) !== String(patch.id)) {
      return prev;
    }

    return {
      ...prev,
      ...(patch || {}),
    };
  });
}

function handleParameterManagerUpdateNode(nodeId, patch) {
  if (!nodeId) return;

  applyNodePatch({
    id: nodeId,
    ...(patch || {}),
  });

  setSelectedId(nodeId);
  setSelectedIds([nodeId]);
}

  const resolvedNodes = useMemo(
    () =>
      resolveNodeTree({
        rootInstances: nodes,
        definitions,
        globalParameters: projectParameters,
      }),
    [nodes, definitions, projectParameters]
  );

  useEffect(() => {
  if (!selectedId) {
    setResolvedNode(null);
    return;
  }

  const next = findResolvedNodeById(resolvedNodes, selectedId);
  if (next) {
    setResolvedNode(next);
  }
}, [resolvedNodes, selectedId]);

 const selectedNode = useMemo(() => {
  return findNodeInTree(nodes, selectedId);
}, [nodes, selectedId]);

const simpleResolvedNode = useMemo(() => {
  if (!resolvedNode) return null;

  const category = String(
    resolvedNode.category || resolvedNode.definition?.category || ""
  ).toLowerCase();

  if (category.includes("cabinet")) {
    return resolvedNode;
  }

  if (resolvedNode.parentGeneratedFromId) {
    const parent = findResolvedNodeById(
      resolvedNodes,
      resolvedNode.parentGeneratedFromId
    );

    if (parent) return parent;
  }

  return resolvedNode;
}, [resolvedNode, resolvedNodes]);

const simpleSelectedNode = useMemo(() => {
  if (!simpleResolvedNode?.id) {
    return selectedNode;
  }

  return findNodeInTree(nodes, simpleResolvedNode.id) || selectedNode;
}, [nodes, simpleResolvedNode, selectedNode]);

function getRemovableSelectionIds() {
  const rawIds = selectedIds.length
    ? selectedIds
    : selectedId
    ? [selectedId]
    : [];

  const mappedIds = rawIds
    .map((id) => {
      const resolved = findResolvedNodeById(resolvedNodes, id);

      // Ako je selektovan generated board/front/shelf,
      // brišemo njegov parent cabinet.
      if (resolved?.parentGeneratedFromId) {
        return resolved.parentGeneratedFromId;
      }

      return id;
    })
    .filter(Boolean);

  return Array.from(new Set(mappedIds.map(String)));
}

  function applyNodeTransform(nodeId, transform) {
  applyNodePatch({
    id: nodeId,
    transform,
  });
}

const isolatedNodeId = isolateMode
  ? simpleResolvedNode?.id || resolvedNode?.id || selectedId
  : null;

  const activeSelectionLabel =
  simpleResolvedNode?.name ||
  resolvedNode?.name ||
  selectedId ||
  "";

  const canRemoveSelection = getRemovableSelectionIds().length > 0;

const isPresentMode = workspaceMode === "present";
const isDocumentationMode = workspaceMode === "documentation";
const showEditorPanels = !isPresentMode && !isDocumentationMode;

function applyProjectParametersChange(updater) {
  applyProjectChange(() => {
    setProjectParameters((prev) => {
      if (typeof updater === "function") {
        return updater(prev);
      }

      return updater;
    });
  });
}

  function addChildToSelectedNode() {
    if (!selectedId) {
      alert("Select parent node first.");
      return;
    }

    const boxDefinition = definitions.find(
      (d) => String(d.category || "") === "primitive.box"
    );

    if (!boxDefinition) {
      alert("Create Test Box definition first.");
      return;
    }

    const childNode = createInstanceNode();
    childNode.definitionId = boxDefinition.id;
    childNode.name = "Child Box";
    childNode.transform = {
      position: { x: 300, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
    };
    childNode.parameterBindings = {};
    childNode.parameterOverrides = {
      width: 300,
      height: 300,
      depth: 300,
    };
    childNode.placement = {
      mode: "manual",
      face: "center",
    };
    childNode.children = [];

    applyProjectChange(() => {
      setNodes((prev) =>
        updateNodeInTree(prev, selectedId, (node) => ({
          ...node,
          children: [...(node.children || []), childNode],
        }))
      );
    });

    setSelectedId(childNode.id);
    setResolvedNode(null);
  }

  function findRoomWallById(id) {
  return (room?.walls || []).find((wall) => String(wall.id) === String(id));
}

function isCabinetDefinition(definition) {
  return String(definition?.category || "")
    .toLowerCase()
    .includes("cabinet");
}

function getCabinetDefaultsByType(type = "base") {
  const normalized = String(type || "base").toLowerCase();

  if (normalized === "wall") {
    return {
      width: 600,
      height: 700,
      depth: 320,
      elevation: Number(room?.wallCabinetOffset ?? 1500),
      cabinetRole: "standard",
      applianceType: "none",
      frontType: "doors",
    };
  }

  if (normalized === "tall") {
    return {
      width: 600,
      height: 2100,
      depth: 560,
      elevation: Number(room?.baseFloorOffset ?? 150),
      cabinetRole: "standard",
      applianceType: "none",
      frontType: "doors",
    };
  }

  return {
    width: 600,
    height: 720,
    depth: 560,
    elevation: Number(room?.baseFloorOffset ?? 150),
    cabinetRole: "standard",
    applianceType: "none",
    frontType: "doors",
  };
}

function inferCabinetTypeFromDefinition(definition) {
  const category = String(definition?.category || "").toLowerCase();
  const name = String(definition?.name || definition?.code || "").toLowerCase();

  if (category.includes("wall") || name.includes("wall")) return "wall";
  if (category.includes("tall") || name.includes("tall")) return "tall";

  return "base";
}

function getNextFreeOffsetOnWall({ wallId, elevation, depth }) {
  const sameWallCabinets = (nodes || [])
    .filter((node) => {
      if (String(node?.placement?.mode) !== "wall") return false;
      if (String(node?.placement?.wallId) !== String(wallId)) return false;

      const nodeElevation = Number(
        node?.placement?.elevation ??
          node?.parameterOverrides?.floorOffset ??
          0
      );

      const nodeDepth = Number(node?.parameterOverrides?.depth ?? 0);

      // Razdvajamo base/wall/tall po elevation/depth da ne guramo wall elemente iza base elemenata.
      return (
        Math.abs(nodeElevation - Number(elevation || 0)) < 10 &&
        Math.abs(nodeDepth - Number(depth || 0)) < 80
      );
    })
    .sort(
      (a, b) =>
        Number(a?.placement?.offsetFromWallStart || 0) -
        Number(b?.placement?.offsetFromWallStart || 0)
    );

  if (!sameWallCabinets.length) return 0;

  const last = sameWallCabinets[sameWallCabinets.length - 1];

  return (
    Number(last?.placement?.offsetFromWallStart || 0) +
    Number(last?.parameterOverrides?.width || 600)
  );
}

function createWallAttachedCabinetNode(definition, options = {}) {
  const wall = findRoomWallById(selectedRoomObjectId);

  if (!wall) {
    alert("Select a wall first.");
    return null;
  }

  const cabinetType = options.type || inferCabinetTypeFromDefinition(definition);
  const defaults = getCabinetDefaultsByType(cabinetType);

  const width = Number(options.width ?? defaults.width);
  const height = Number(options.height ?? defaults.height);
  const depth = Number(options.depth ?? defaults.depth);
  const elevation = Number(options.elevation ?? defaults.elevation);

  const offsetFromWallStart =
    options.offsetFromWallStart ??
    getNextFreeOffsetOnWall({
      wallId: wall.id,
      elevation,
      depth,
    });

  const placementResult = resolveWallPlacement({
    wall,
    offsetFromWallStart,
    width,
    height,
    depth,
    elevation,
    snap: 10,
    clampToWall: true,
  });

  if (!placementResult) {
    alert("Cannot resolve wall placement.");
    return null;
  }

  const node = createRootInstanceFromDefinition({
    definition,
    options,
    nodes,
    definitions,
    selectedId,
    projectParameters,
  });

  node.name =
    options.name ||
    definition.name ||
    (cabinetType === "wall"
      ? "Wall cabinet"
      : cabinetType === "tall"
      ? "Tall cabinet"
      : "Base cabinet");

  node.placement = {
    ...placementResult.placement,
    wallId: wall.id,
    alignBackToWall: true,
  };

  node.transform = placementResult.transform;

  node.parameterOverrides = {
    ...(node.parameterOverrides || {}),
    width,
    height,
    depth,
    floorOffset: elevation,
    cabinetRole: options.cabinetRole || defaults.cabinetRole,
    applianceType: options.applianceType || defaults.applianceType,
    frontType: options.frontType || defaults.frontType,
  };

  return node;
}

function handleAddDefinition(definition, options = {}) {
  const selectedWall = findRoomWallById(selectedRoomObjectId);

  if (selectedWall && isCabinetDefinition(definition)) {
    const node = createWallAttachedCabinetNode(definition, options);

    if (!node) return;

    applyProjectChange(() => {
      setNodes((prev) => [...prev, node]);
    });

    setSelectedId(node.id);
    setSelectedIds([node.id]);
    setResolvedNode(null);
    setLeftTab("browser");
    applyWorkspaceMode("design");

    return;
  }

  addRootInstance(definition, options);
}

function addRootInstance(definition, options = {}) {
  if (!canDefinitionContain("project", definition.nodeType)) {
    alert(`Type "${definition.nodeType}" is not allowed in project scene.`);
    return;
  }

  const node = createRootInstanceFromDefinition({
    definition,
    options,
    nodes,
    definitions,
    selectedId,
    projectParameters,
  });

  applyProjectChange(() => {
    setNodes((prev) => [...prev, node]);
  });

  setSelectedId(node.id);
  setSelectedIds([node.id]);
  setResolvedNode(null);
}

function clearSelection() {
  setSelectedId(null);
  setResolvedNode(null);
  setSelectedIds([]);
}

function pasteNodes(pasted = []) {
  if (!pasted.length) return;

  applyProjectChange(() => {
    setNodes((prev) => [...prev, ...pasted]);
  });

  const newIds = pasted.map((node) => node.id);

  setSelectedIds(newIds);
  setSelectedId(newIds[0] || null);
  setResolvedNode(null);
}

 function removeSelectedNode() {
  const idsToRemove = getRemovableSelectionIds();

  if (!idsToRemove.length) return;

  applyProjectChange(() => {
    setNodes((prev) => removeNodesByIds(prev, idsToRemove));
  });

  setSelectedId(null);
  setSelectedIds([]);
  setResolvedNode(null);
  setSelectedPartition(null);
}

function handleApplyGeneratedScene({
  mode = "append",
  nodes: generatedNodes = [],
  projectParametersPatch = {},
}) {
  if (!generatedNodes.length) return;

  const nextGeneratedNodes = structuredClone(generatedNodes);

  applyProjectChange(() => {
    setProjectParameters((prev) => ({
      ...(prev || {}),
      ...(projectParametersPatch || {}),
    }));

    setNodes((prev) => {
      if (mode === "replace") {
        return nextGeneratedNodes;
      }

      return [...prev, ...nextGeneratedNodes];
    });
  });

  const newIds = nextGeneratedNodes.map((node) => node.id);

  setSelectedIds(newIds);
  setSelectedId(newIds[0] || null);
  setResolvedNode(null);
  setSelectedPartition(null);
  setLeftTab("browser");

  applyWorkspaceMode("present");
}


async function handleSave() {
  const result = await saveProject({
  project,
  projectName,
  projectDescription,
  projectParameters,
  room,
  nodes,
  resolvedNodes,
  documentation,
});

  if (result?.validationErrors?.length) {
    alert(
      result.validationErrors
        .map((e) => `${e.node} / ${e.key}: ${e.message} (${e.raw})`)
        .join("\n")
    );
    return;
  }

  if (result?.error) {
    alert("Save failed.");
    return;
  }

  if (result?.ok) {
    clearHistory();
  }
}

async function handleDeleteProject() {
  if (!project) return;

  const confirmed = window.confirm(`Delete project "${project.name}"?`);
  if (!confirmed) return;

  const result = await deleteProject(project);

  if (!result.ok) {
    alert("Delete failed.");
    return;
  }

  navigate("/interior-editor/projects");
}

  const addableDefinitions = definitions.filter((d) =>
    canDefinitionContain("project", d.nodeType)
  );

  useEditorKeyboardShortcuts({
  selectedId,
  selectedIds,

  clipboard,
  setClipboard,

  getSelectedNodes,
  onDeleteSelection: removeSelectedNode,
  onClearSelection: clearSelection,
  onPasteNodes: pasteNodes,
});

function openSaveTemplateModal() {
  if (!resolvedNode) {
    alert("Select element first.");
    return;
  }

  const baseName = resolvedNode.name || "Template";

  const defaultCategory =
    categories.find((c) => c.category_key === "base.cabinet")?.category_key ||
    categories[0]?.category_key ||
    "";

  setTemplateForm({
    name: baseName,
    code: `${String(baseName)
      .toLowerCase()
      .replaceAll(" ", "_")}_${Date.now()}`,
    category: defaultCategory,
    description: "",
  });

  setShowSaveTemplateModal(true);
}

  if (loading) {
    return (
      <div className="h-[calc(100vh-80px)] flex items-center justify-center text-slate-500">
        Loading project editor...
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-80px)] overflow-hidden flex flex-col bg-slate-100">
      <EditorTopBar
  projectName={projectName}
  projectDescription={projectDescription}
  onProjectNameChange={setProjectName}
  onProjectDescriptionChange={setProjectDescription}
  saving={saving}
  canUndo={history.length > 0}
  canRedo={future.length > 0}
  onUndo={undo}
  onRedo={redo}
  onSave={handleSave}
  onDeleteProject={handleDeleteProject}
  onOpenParameterManager={() => setShowParameterManager(true)}
  onNavigate={navigate}
/>

      <EditorRibbon
      workspaceMode={workspaceMode}
      onWorkspaceModeChange={applyWorkspaceMode}
      toolMode={toolMode}
      setToolMode={setToolMode}
      viewMode={viewMode}
      setViewMode={setViewMode}
      isolateMode={isolateMode}
      setIsolateMode={setIsolateMode}
      canIsolate={Boolean(simpleResolvedNode || resolvedNode || selectedId)}
      selectedLabel={resolvedNode?.name || selectedId || "none"}
      onOpenLibrary={() => setLeftTab("library")}
      onSaveTemplate={openSaveTemplateModal}
      onAddChild={addChildToSelectedNode}
      onRemove={removeSelectedNode}
      canSaveTemplate={Boolean(resolvedNode)}
      canRemove={canRemoveSelection}

      sceneViewSettings={sceneViewSettings}
  onSceneViewSettingsChange={setSceneViewSettings}
      />

      {/* MAIN WORKSPACE */}
      <div
  className={[
    "flex-1 min-h-0 grid overflow-hidden",
    showEditorPanels
      ? "grid-cols-[300px_minmax(0,1fr)_360px]"
      : "grid-cols-[minmax(0,1fr)]",
  ].join(" ")}
>
        {/* LEFT PANEL */}
        {showEditorPanels && (
  <EditorLeftPanel
    activeTab={leftTab}
    onChangeTab={setLeftTab}
    resolvedNodes={resolvedNodes}
    selectedId={selectedId}
    onSelectNode={(id) => {
      setSelectedId(id);
      setSelectedIds([id]);
      setResolvedNode(findResolvedNodeById(resolvedNodes, id));
    }}
    onOpenNode={(node) => {
  openResolvedNodeEditor(node, "auto");
}}
    onAddChild={addChildToSelectedNode}
    definitions={addableDefinitions}
    templates={templates}
    onAddDefinition={handleAddDefinition}
    onAddTemplate={addTemplateInstance}
  />
)}

        {/* CENTER SCENE */}
        <main className="relative min-h-0 overflow-hidden bg-slate-200">

{!isDocumentationMode && (
  <div className="absolute left-4 top-16 z-50 flex gap-2">
    <button
      type="button"
      onClick={() => setShowMountingDrillings((v) => !v)}
      className={[
        "px-3 py-2 border rounded text-sm shadow",
        showMountingDrillings
          ? "bg-blue-600 text-white border-blue-600"
          : "bg-white text-slate-700 border-slate-200",
      ].join(" ")}
    >
      {showMountingDrillings ? "Hide drillings" : "Show drillings"}
    </button>
  </div>
)}

          {isDocumentationMode ? (
  <DrawingSheetPage
    rootInstances={nodes}
    definitions={definitions}
    globalParameters={projectParameters}
    room={room}
    selectedRoomObjectId={selectedRoomObjectId}
    onSelectRoomObject={setSelectedRoomObjectId}
    selectedId={selectedId}
    selectedIds={selectedIds}
    onMultiSelect={setSelectedIds}
    onSelect={setSelectedId}
    onResolvedSelect={setResolvedNode}
    onDoubleOpen={(node) => {
      openResolvedNodeEditor(node, "auto");
    }}
    onTransformNode={applyNodeTransform}
    onChangeNode={applyNodePatch}
    materials={materials}
    documentData={documentation}
    onDocumentDataChange={setDocumentation}
  />
) : (
  <HierarchySceneCanvas
    rootInstances={nodes}
    definitions={definitions}
    globalParameters={projectParameters}
    room={room}
    selectedRoomObjectId={selectedRoomObjectId}
    onSelectRoomObject={setSelectedRoomObjectId}
    selectedId={selectedId}
    selectedIds={selectedIds}
    onMultiSelect={setSelectedIds}
    toolMode={toolMode}
    setToolMode={setToolMode}
    viewMode={viewMode}
    setViewMode={setViewMode}
    isolatedNodeId={isolatedNodeId}
    materials={materials}
    onSelect={setSelectedId}
    onResolvedSelect={setResolvedNode}
    selectedPartition={selectedPartition}
    onSelectPartition={setSelectedPartition}
    onTransformNode={applyNodeTransform}
    onDoubleOpen={(node) => {
      openResolvedNodeEditor(node, "auto");
    }}
    onChangeNode={applyNodePatch}
    showMountingDrillings={showMountingDrillings}
    sceneViewSettings={sceneViewSettings}
    onSceneViewSettingsChange={setSceneViewSettings}
  />
)}

{!isDocumentationMode && isPresentMode && (
  <button
    type="button"
    onClick={() => applyWorkspaceMode("design")}
    className="absolute right-4 top-4 z-30 px-4 py-2 rounded-lg border bg-white/90 shadow text-sm text-slate-700 hover:bg-white"
  >
    Exit Present
  </button>
)}

<SelectionActionBar
    visible={
  showEditorPanels &&
  Boolean(simpleResolvedNode || resolvedNode || selectedId)
}
onOpenSource={() => openResolvedNodeEditor(resolvedNode, "auto")}
    selectedLabel={activeSelectionLabel}
    onQuickAdd={handleQuickAdd}
    onInspect={() => {
      setToolMode("select");
      setViewMode("xray");
      setIsolateMode(true);
    }}
    onGridEdit={() => {
      setToolMode("partition");
      setViewMode("box");
      setIsolateMode(true);
    }}
    onPresent={() => {
      setToolMode("select");
      setViewMode("realistic");
      setIsolateMode(false);
    }}
    onDelete={removeSelectedNode}
    onClearSelection={clearSelection}
    canRemove={canRemoveSelection}
  />
        </main>

        {/* RIGHT PROPERTIES */}
{showEditorPanels && (
  <aside className="min-h-0 overflow-hidden border-l bg-white flex flex-col">
    <EditorRightPanel
  selectedNode={selectedNode}
  resolvedNode={resolvedNode}
  simpleSelectedNode={simpleSelectedNode}
  simpleResolvedNode={simpleResolvedNode}
  definitions={definitions}
  projectParameters={projectParameters}
  setProjectParameters={applyProjectParametersChange}
  room={room}
  setRoom={setRoom}
  selectedRoomObjectId={selectedRoomObjectId}
  setSelectedRoomObjectId={setSelectedRoomObjectId}
  onQuickAdd={handleQuickAdd}
  onChangeNode={applyNodePatch}
  onApplyGeneratedScene={handleApplyGeneratedScene}
  onApplySceneEditActions={handleApplySceneEditActions}
  onOpenResolvedSource={openResolvedNodeEditor}
/>
  </aside>
)}
      </div>
      

      {/* STATUS BAR */}
      <div className="h-6 shrink-0 border-t bg-white px-3 flex items-center justify-between text-[11px] text-slate-500">
        <div>
          Nodes: {nodes.length} | Definitions: {definitions.length}
        </div>
        <div>
          MODULAR OBJECTS LLC · Interior Editor
        </div>
      </div>

      {showSaveTemplateModal && (
  <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
    <div className="bg-white rounded shadow-lg w-[420px] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-lg">Save as Template</div>
        <button
          type="button"
          onClick={() => setShowSaveTemplateModal(false)}
          className="px-3 py-1 border rounded"
        >
          Close
        </button>
      </div>

      <input
        value={templateForm.name}
        onChange={(e) =>
          setTemplateForm((p) => ({ ...p, name: e.target.value }))
        }
        placeholder="Template name"
        className="w-full px-3 py-2 border rounded"
      />

      <input
        value={templateForm.code}
        onChange={(e) =>
          setTemplateForm((p) => ({ ...p, code: e.target.value }))
        }
        placeholder="Template code"
        className="w-full px-3 py-2 border rounded"
      />

      <select
  value={templateForm.category || ""}
  onChange={(e) =>
    setTemplateForm((p) => ({ ...p, category: e.target.value }))
  }
  className="w-full px-3 py-2 border rounded bg-white"
>
  <option value="">Select category...</option>

  {categories.map((c) => (
    <option key={c.id} value={c.category_key}>
      {c.label}
    </option>
  ))}
</select>

      <textarea
        value={templateForm.description}
        onChange={(e) =>
          setTemplateForm((p) => ({ ...p, description: e.target.value }))
        }
        placeholder="Description"
        className="w-full px-3 py-2 border rounded h-24"
      />

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={() => setShowSaveTemplateModal(false)}
          className="px-4 py-2 border rounded"
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={saveSelectedAsTemplate}
          className="px-4 py-2 bg-blue-600 text-white rounded"
        >
          Save Template
        </button>
      </div>
    </div>
  </div>
)}

      {showParameterManager && (
 <ParameterManagerModal
  isOpen={showParameterManager}
  onClose={() => setShowParameterManager(false)}
  projectParameters={projectParameters}
  onChangeProjectParameters={applyProjectParametersChange}
  resolvedNode={
    resolvedNode
      ? {
          ...resolvedNode,
          node: selectedNode || resolvedNode,
        }
      : {
          name: projectName || "Project",
          effectiveParameters: projectParameters,
          resolvedParameters: Object.entries(projectParameters || {}).map(
            ([key, value]) => ({
              key,
              value,
              source: "project",
            })
          ),
          parameterDebug: {
            globalParameters: projectParameters,
            effectiveParameters: projectParameters,
          },
          node: null,
          definition: null,
        }
  }
  onUpdateNode={handleParameterManagerUpdateNode}
/>
)}
    </div>
  );
}